import { supabase } from '../lib/supabase';
import { isSeasonPastForUser } from '../config/seasons';

/**
 * Report Service
 *
 * Reads for the post-event judging report and the "Past Competitions" profile
 * section. Everything here is read-only: judging_reports rows are inserted by
 * hand in the SQL editor, and RLS only returns rows whose `published_at` is
 * set — so "no report rows came back" simply means "not released yet".
 */

const REPORT_SUMMARY_COLUMNS = 'id, variation_id, competition_year, rank, rank_group_size, total_score, total_max, published_at';

/**
 * Everything the /report/:registrationId page needs, in one round trip.
 * RLS guarantees the caller owns the registration; a foreign id resolves to
 * `registration: null`.
 */
export const getReportBundle = async (registrationId) => {
  const { data, error } = await supabase
    .from('registrations')
    .select(`
      id, competition_year, registration_number, registration_type, participation_status,
      dancer_information ( first_name, last_name ),
      variations ( id, variation_number, variation_name, ballet_origin, character_name, age_division, performance_category ),
      judging_reports ( ${REPORT_SUMMARY_COLUMNS}, scores, feedback )
    `)
    .eq('id', registrationId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return { registration: null, dancerInfo: null, variations: [], reports: [] };

  const { dancer_information, variations, judging_reports, ...registration } = data;
  return {
    registration,
    dancerInfo: Array.isArray(dancer_information) ? dancer_information[0] : dancer_information,
    variations: (variations || []).slice().sort((a, b) => (a.variation_number || 0) - (b.variation_number || 0)),
    reports: judging_reports || [],
  };
};

/**
 * Registrations whose season's competition day has passed — the rows that
 * belong in "Past Competitions". Test accounts and never-completed drafts are
 * excluded; withdrawn / deferred / no-show rows are kept so the dancer still
 * sees their history (the card labels them).
 *
 * Each row carries nested dancer_information, variations and (published)
 * judging_reports so the card renders with no further fetches.
 *
 * `email` lets PAST_PREVIEW_EMAILS testers see the past view early.
 */
export const getPastRegistrations = async (userId, email, now = new Date()) => {
  const { data, error } = await supabase
    .from('registrations')
    .select(`
      id, competition_year, registration_number, registration_type, status,
      payment_status, participation_status, submitted_at, created_at,
      dancer_information ( first_name, last_name ),
      variations ( id, variation_number, variation_name, ballet_origin, character_name, age_division, performance_category ),
      judging_reports ( ${REPORT_SUMMARY_COLUMNS} )
    `)
    .eq('user_id', userId)
    .eq('registration_type', 'individual')
    .eq('status', 'complete')
    .eq('payment_status', 'paid')
    .neq('participation_status', 'test')
    .order('competition_year', { ascending: false });

  if (error) throw error;

  return (data || [])
    .filter((r) => isSeasonPastForUser(r.competition_year, email, now))
    .map((r) => ({
      ...r,
      dancer_information: Array.isArray(r.dancer_information) ? r.dancer_information[0] : r.dancer_information,
      variations: (r.variations || []).slice().sort((a, b) => (a.variation_number || 0) - (b.variation_number || 0)),
      judging_reports: r.judging_reports || [],
    }));
};

/** True when at least one published report exists on a registration row. */
export const hasPublishedReport = (registration) =>
  Array.isArray(registration?.judging_reports) && registration.judging_reports.some((r) => r.published_at);
